import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const show = (v, labels = {}) => (Array.isArray(v) ? (v.length ? v.map((x) => labels[x] || x).join(", ") : "none") : (labels[v] || v) ?? "—");

const Side = ({ title, items, testid }) => (
    <div className="border rounded-md p-3 text-xs space-y-1" data-testid={testid}>
        <div className="font-medium text-gray-900 text-sm">{title}</div>
        {items.map(([k, v]) => <div key={k}><span className="text-muted-foreground">{k}: </span>{show(v)}</div>)}
    </div>
);

// Comparison + explicit confirmation for adding a pre-existing lead to this import.
export const AddExistingLeadDialog = ({ batchId, row, onClose, onDone }) => {
    const [cmp, setCmp] = useState(null);
    const [apply, setApply] = useState({});
    const [ack, setAck] = useState(false);
    const [busy, setBusy] = useState(false);
    const [reqId] = useState(() => crypto.randomUUID());

    const load = async () => {
        try {
            const { data } = await api.get(`/imports/${batchId}/results/${row.row}/compare`);
            setCmp(data);
            setApply(Object.fromEntries(data.changes.filter((c) => !c.required).map((c) => [c.field, !!c.default])));
            setAck(false);
        } catch (e) { toast.error(formatApiError(e)); onClose(); }
    };
    useEffect(() => { load(); }, [row.row]); // eslint-disable-line react-hooks/exhaustive-deps

    const confirm = async () => {
        setBusy(true);
        try {
            await api.post(`/imports/${batchId}/results/${row.row}/add-existing`, {
                fingerprint: cmp.fingerprint, apply: Object.keys(apply).filter((k) => apply[k]),
                acknowledge_conflicts: ack, request_id: reqId,
            });
            toast.success("Existing lead added to this import");
            onDone();
        } catch (e) {
            const d = e?.response?.data?.detail;
            toast.error(d?.message || formatApiError(e));
            if (d?.code === "lead_changed") await load();
        } finally { setBusy(false); }
    };

    const ex = cmp?.existing; const inc = cmp?.row;
    return (
        <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="add-existing-dialog">
                <DialogHeader>
                    <DialogTitle>Add existing lead to this import?</DialogTitle>
                    <DialogDescription>The lead keeps its ID, source, status and all Communication History.</DialogDescription>
                </DialogHeader>
                {!cmp ? <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div> : (
                    <div className="space-y-4 text-sm">
                        <div className="grid sm:grid-cols-3 gap-3">
                            <Side title="Existing lead" testid="cmp-existing" items={[["Name", ex.name], ["Case ID", ex.case_id], ["Phone", ex.phone], ["Source", ex.source], ["Status", ex.status], ["Lead stage", ex.lead_status]]} />
                            <Side title={`Incoming CSV row ${inc.row}`} testid="cmp-incoming" items={[["Name", inc.name], ["Case ID", inc.case_id], ["Phone", inc.phone_supplied], ["Matched on", inc.match_basis]]} />
                            <Side title="This import" testid="cmp-import" items={[["File", cmp.import.filename], ["Lead Type", cmp.import.lead_type?.name], ["Status", cmp.import.status_note]]} />
                        </div>
                        {cmp.conflicts.length > 0 && (
                            <div className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-red-800 space-y-1" data-testid="cmp-conflicts">
                                <div className="font-medium">Possible identity conflict. Review Later is recommended.</div>
                                {cmp.conflicts.map((c) => <div key={c.code}>{c.text}</div>)}
                                <label className="flex items-center gap-2 pt-1">
                                    <Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="cmp-ack-conflict" />
                                    I've checked these are the same person.
                                </label>
                            </div>
                        )}
                        <div data-testid="cmp-changes">
                            <div className="font-medium mb-1">Will change</div>
                            {cmp.changes.map((c) => (
                                <label key={c.field} className="flex items-start gap-2 py-1" data-testid={`change-${c.field}`}>
                                    <Checkbox checked={c.required || !!apply[c.field]} disabled={c.required}
                                        onCheckedChange={(v) => setApply({ ...apply, [c.field]: !!v })} data-testid={`change-${c.field}-check`} />
                                    <span><b>{c.label}:</b> {c.field === "name" || c.field === "case_id" ? `\u201c${show(c.from)}\u201d \u2192 \u201c${c.to}\u201d` : `${show(c.from, cmp.labels)} \u2192 ${show(c.to, cmp.labels)}`}
                                        {c.required && <span className="text-muted-foreground"> (required)</span>}</span>
                                </label>
                            ))}
                        </div>
                        <div data-testid="cmp-unchanged">
                            <div className="font-medium mb-1">Stays the same</div>
                            <div className="grid sm:grid-cols-2 gap-x-4 text-xs text-gray-700">
                                {cmp.unchanged.map((u) => <div key={u.field}>{u.label}: {u.note || show(u.value)}</div>)}
                            </div>
                        </div>
                    </div>
                )}
                <DialogFooter className="gap-2">
                    <Button variant="ghost" onClick={onClose} disabled={busy} data-testid="add-existing-cancel">Cancel</Button>
                    <Button onClick={confirm} disabled={!cmp || busy || (cmp.conflicts.length > 0 && !ack)} data-testid="add-existing-confirm">
                        {busy ? "Adding…" : "Confirm and add"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
